import { dryrunDexiCU } from "./ao-connection"
import { supabase } from "./supabase"
import { Bar, ResolutionString } from "../charting_library/charting_library"
import { Candle, Tag } from "../types"

export type PoolStats = {
  totalTraders: number
  sellVolume: number
  sellers: number
  sellCount: number
  buyVolume: number
  buyers: number
  buyCount: number
  totalVolume: number
}

export async function getPoolStats(poolId: string): Promise<PoolStats> {
  const { data, error } = await supabase.functions.invoke("stats", {
    body: {
      action: "aggregate",
      ammProcessId: poolId,
      minTimestamp: 0,
    },
  })

  if (error) {
    throw new Error(`Supabase function error: ${error.message}`)
  }

  if (!data) {
    throw new Error("No response from Get-Stats (pool)")
  }

  const stats = data as {
    total_volume: number | null
    sell_volume: number | null
    buy_volume: number | null
    sell_count: number | null
    buy_count: number | null
    distinct_sellers: number | null
    distinct_buyers: number | null
    distinct_traders: number | null
  }

  return {
    totalTraders: stats.distinct_traders || 0,
    sellVolume: stats.sell_volume || 0,
    sellers: stats.distinct_sellers || 0,
    sellCount: stats.sell_count || 0,
    buyVolume: stats.buy_volume || 0,
    buyers: stats.distinct_buyers || 0,
    buyCount: stats.buy_count || 0,
    totalVolume: stats.total_volume || 0,
  }
}

type Interval =
  | "15m"
  // | "30m"
  // | "45m"
  | "1h"
  // | "2h"
  // | "3h"
  | "4h"
  | "1d"
// | "1w"
// | "1m"

// Supported resolutions: 15, 30, 45, 60, 120, 180, 240, 1D, 1W, 1M
function getInterval(resolution: ResolutionString): Interval {
  switch (resolution) {
    case "15":
      return "15m"
    case "30":
      return "15m"
    case "45":
      return "15m"
    case "60":
      return "1h"
    case "120":
      return "1h"
    case "180":
      return "1h"
    case "240":
      return "4h"
    case "1D":
      return "1d"
    case "1W":
      return "1d"
    case "1M":
      return "1d"
    default:
      throw new Error(`Unsupported resolution: ${resolution}`)
  }
}

export async function getCandles(
  poolId: string,
  resolution: ResolutionString,
  useCurrency: boolean,
  reverseChart = false,
  useDay = false,
): Promise<Bar[]> {
  const interval = getInterval(resolution)

  const params: Record<string, any> = {
    amm: poolId,
    interval: interval,
    days: useDay ? "1" : "90",
  }

  if (useCurrency) {
    params.convertToUsd = true
  }

  if (reverseChart) {
    params.inverseChart = "true"
  }

  const { data, error } = await supabase.functions.invoke("candles", {
    body: params,
  })

  if (error) {
    throw new Error(`Supabase function error: ${error.message}`)
  }

  if (!data) {
    throw new Error("No response from Get-Candles (pools)")
  }

  const candles = data as Candle[]

  const bars: Bar[] = candles.map((candle) => ({
    time: candle.start_timestamp * 1000,
    open: candle.open,
    high: candle.high,
    low: candle.low,
    close: candle.close,
    volume: candle.volume,
  }))

  return bars
}
